import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, cn, FormatUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api';
import { Icon, Text } from '@shared/widgets';
import CardIcon from '@assets/icons/card.svg';

interface AccountCardTileProps {
  className?: string;
  account: AccountResponse;
  noHover?: boolean;
}

export const AccountCardTile = (props: AccountCardTileProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('accounts');

  const accountFlow = useAccountFlow(profileFeatureStore.data?.accountFlows, props.account.id);
  const symbol = useCurrencySymbol(props.account.currency);
  const progress = useMemo(() => {
    if (props.account.balance) return props.account.balance / (props.account.creditLimit / 100);
    return 0;
  }, [props.account]);

  if (accountFlow?.view === false) return null;
  return (
    <div
      className={cn(
        'flex flex-col bg-white transition-shadow duration-200 dark:bg-soft-black',
        !props.noHover && 'hover:shadow-lg dark:hover:shadow-lg-dark',
        props.className,
      )}
    >
      <div className='flex justify-between'>
        <div className='ml-5 mt-5 flex flex-col'>
          <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)}</Text>
          <Text className='mt-2' weight='medium' size='xs' color='secondary'>
            {props.account.name}
          </Text>
          <Text className='mt-1' weight='medium' size='xs' color='secondary'>
            {props.account.productName}
          </Text>
          <Text className='mt-1' weight='medium'>
            {props.account.name}
          </Text>
        </div>
        <div
          className={cn(
            'relative flex h-32 w-36 flex-none flex-col items-center justify-end overflow-hidden rounded-tr-sm pb-2 pl-9 pr-1 pt-4 text-center',
            'before:absolute before:bottom-[-2px] before:right-[calc(100%-2px)] before:z-[1] before:h-[150%] before:w-1/2 before:origin-bottom-right before:rotate-[20deg] before:border before:bg-white before:dark:bg-soft-black',
            PaletteUtils.isInsigniaPalette && 'bg-soft-black before:border-r-primary dark:bg-slate',
            PaletteUtils.isVcfcPalette && 'bg-primary dark:before:border-r-ice',
          )}
        >
          <div
            className={cn(
              'mb-1',
              'text-[58px] before:leading-[1] before:content-[attr(data-icon)]',
              'after:absolute after:bottom-[-28px] after:left-[7px] after:text-[104px] after:leading-[1] after:opacity-5 after:content-[attr(data-icon)]',
              PaletteUtils.isInsigniaPalette && 'before:text-primary after:text-primary',
              PaletteUtils.isVcfcPalette && 'before:text-white after:text-white',
            )}
            data-icon={symbol}
          />
          <Text weight='semibold' size='sm' color='white'>
            {t(`statuses.account.${props.account.status}`, { ns: 'common' })}
          </Text>
        </div>
      </div>
      <div className='flex flex-col px-5 py-4'>
        <div className='relative h-1 w-full rounded bg-light'>
          <div className='absolute left-0 h-full rounded bg-primary' style={{ width: `${progress}%` }} />
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color='secondary'>
            {t('card.tile.fields.spendingLimit')}
          </Text>
          <Text weight='medium' size='xs'>
            {`${AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)} / ${AmountUtils.transformToAmountWithCurrency(props.account.creditLimit, props.account.currency)}`}
          </Text>
        </div>
        <div className='my-3 h-[1px] w-full bg-gray-black' />
        <div className='flex justify-between'>
          <Text size='sm' color='secondary'>
            {t('card.tile.fields.accountNumber')}
          </Text>
          <Text weight='medium' size='xs'>
            {FormatUtils.toSliceNumber(props.account.accountNumber)}
          </Text>
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color='secondary'>
            {t('card.tile.fields.cards')}
          </Text>
          <div className='flex gap-1'>
            <Text weight='medium' size='xs'>
              {props.account.cardsCount}
            </Text>
            <Icon icon={<CardIcon width='16' height='16' />} />
          </div>
        </div>
      </div>
    </div>
  );
};
